import { DateTimeQuestionDTO } from 'jobs/dtos/questions'
import { QuestionValidationResult } from 'jobs/questions/QuestionValidationResult'
import QuestionViewModelBase, {
  QuestionViewModelPort,
} from '../QuestionViewModelBase'
import { action, computed, observable, makeObservable } from 'mobx'
import { maybeParseDate } from 'utils/date-util'
import { isValid } from 'date-fns'
import { QuestionInputValue } from '../input-value-types'

export default class DateTimeQuestionViewModel extends QuestionViewModelBase<DateTimeQuestionDTO> {
  @observable answer?: Date = undefined
  @observable collectTime = false
  @observable disallowFuture = false
  @observable disallowPast = false

  constructor(private dateTimeQuestionView: QuestionViewModelPort) {
    super(dateTimeQuestionView)

    makeObservable(this)
  }

  @computed
  get isAnswered(): boolean {
    return isValid(this.answer)
  }

  @computed
  get asOfDate() {
    return this.adapter.getAsOfDate()
  }

  validate(): QuestionValidationResult {
    return this.isAnswered
      ? QuestionValidationResult.ok()
      : QuestionValidationResult.failed(['This field is required.'])
  }

  protected hydrate(dto: DateTimeQuestionDTO) {
    this.collectTime = Boolean(dto.collect_time)
    this.disallowPast = Boolean(dto.disallow_past)
    this.disallowFuture = Boolean(dto.disallow_future)
  }

  protected textAnswer(): string {
    if (!this.answer) {
      return ''
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...(this.collectTime && {
        hour: 'numeric',
        minute: '2-digit',
      }),
    })

    return formatter.format(this.answer)
  }

  protected serializeAnswer() {
    return this.answer
  }

  protected hydrateAnswer(answer: unknown): void {
    this.answer =
      typeof answer === 'string'
        ? maybeParseDate(answer)
        : answer instanceof Date
          ? answer
          : undefined
  }

  @action.bound
  change(newValue: Date | undefined) {
    if (this.answer === newValue) {
      return
    }

    this.answer = newValue
    this.notify()
  }

  toInputValue(): QuestionInputValue {
    return { type: 'DATE_TIME', value: this.answer }
  }
}
